import React, { useState, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { GetApp } from "@mui/icons-material";
import {
  IconButton,
  Box,
  Typography,
  useTheme,
  Card,
  Avatar,
  ButtonGroup,
  Button,
} from "@mui/material";
import { useResizeObserver } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import useUserData from "../hooks/useUserData";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import pdfIcon from "../Assets/Icons/pdf.png";
import jpgIcon from "../Assets/Icons/jpg.png";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import LoadingPage from "./LoadingPage";
import QRCode from "qrcode";
import BackgroundShape from "../Assets/Background-Shape.png";

pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs";

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const resizeObserverOptions = {};

const maxWidth = 400;

const MyBadge = () => {
  const { t } = useTranslation();
  const { data } = useUserData();
  const parsedData =
    typeof data?.me === "string" ? JSON.parse(data?.me) : data?.me || {};
  const theme = useTheme();

  const { eventId, role, id, name, eventName } = parsedData;
  // const pdfUrl = `https://api-reg.eventegration.com/graphql/badge/6220072402/visitor/4249235908`;
  const pdfUrl = `https://api-reg.eventegration.com/graphql/badge/${eventId}/${role}/${id}`;
  // console.log(pdfUrl);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const [numPages, setNumPages] = useState(null);
  const [containerRef, setContainerRef] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);

  const onResize = useCallback((entries) => {
    const [entry] = entries;

    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  useResizeObserver(containerRef, resizeObserverOptions, onResize);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  // console.log(">>>>", parsedData);
  const handleDownloadPDF = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `${name}_badge_${eventName}_${id}.pdf`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const handleDownloadJPG = async () => {
    const pdf = await pdfjs.getDocument(pdfUrl).promise;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2.0 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport }).promise;

      const imgData = canvas.toDataURL("image/jpeg");
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${name}_badge_${eventName}-${pageNum}.jpg`;
      link.click();
    }
  };
  useEffect(() => {
    const generateQR = async (text) => {
      try {
        return await QRCode.toDataURL(text);
      } catch (err) {
        console.error(err);
        return "";
      }
    };

    const fetchQrCode = async () => {
      const url = await generateQR(
        `${parsedData?.eventId},${parsedData?.role},${parsedData?.id}`
      );
      setQrCodeUrl(url);
    };

    fetchQrCode();
  }, [parsedData]);

  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: t("Dashboard"), href: "/" }]}
        currentPage={t("MyBadge")}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          p: 4,
          background: theme.palette.background.default,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 400,
            p: 4,
            borderRadius: 4,
            boxShadow: theme.shadows[10],
            border: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
            position: "relative",
            overflow: "visible",
            mb: 4,
            backgroundImage:
              theme.palette.mode === "light"
                ? `url(${BackgroundShape})`
                : "none",
            backgroundBlendMode: "multiply",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            color: "#fff",
          }}
        >
          {/* Profile Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            {parsedData.profile_pic ? (
              <Avatar
                src={parsedData.profile_pic}
                sx={{
                  width: 100,
                  height: 100,
                  mb: 3,
                  border: `3px solid ${theme.palette.primary.main}`,
                  boxShadow: theme.shadows[6],
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mb: 3,
                  bgcolor: theme.palette.primary.main,
                  fontSize: "2.5rem",
                  border: `3px solid ${theme.palette.primary.main}`,
                  boxShadow: theme.shadows[6],
                }}
              >
                {name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")}
              </Avatar>
            )}

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                lineHeight: 1.2,
                mb: 1,
                fontSize: name?.length > 20 ? "1.5rem" : "2rem",
              }}
            >
              {name}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                textAlign: "center",
                mb: 1,
              }}
            >
              {parsedData.designation}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textAlign: "center",
                mb: 2,
              }}
            >
              {parsedData.company}
            </Typography>
          </Box>

          {/* QR Code Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.background.default,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <img
              src={qrCodeUrl}
              alt="QR Code"
              style={{
                width: 150,
                height: 150,
                filter: `drop-shadow(0 4px 8px ${theme.palette.action.hover})`,
              }}
            />
          </Box>

          {/* Country */}
          <Typography
            variant="overline"
            sx={{
              display: "block",
              textAlign: "center",
              letterSpacing: 1.5,
              color: theme.palette.text.secondary,
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {parsedData.country}
          </Typography>
        </Card>

        {/* Download Buttons */}
        <ButtonGroup
          variant="contained"
          sx={{
            mt: 2,
            boxShadow: theme.shadows[6],
            borderRadius: 50,
            overflow: "hidden",
          }}
        >
          <Button
            onClick={handleDownloadPDF}
            startIcon={<GetApp />}
            sx={{
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              borderRight: `1px solid ${theme.palette.background.paper}`,
            }}
          >
            {t("Download PDF")}
          </Button>
          <Button
            onClick={handleDownloadJPG}
            startIcon={<GetApp />}
            sx={{
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            {t("Download JPG")}
          </Button>
        </ButtonGroup>
      </Box>
    </>
  );
};

export default MyBadge;
